import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Heart, Menu, Phone, Search, ShoppingBag, User, X } from "lucide-react";
import { CONTACT, SHOP_CATEGORIES } from "../lib/content";
import { useShop } from "../context/ShopContext";
import { api, gbp } from "../lib/api";
import Triskelion from "./Triskelion";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop", mega: true },
  { label: "Collections", to: "/collections" },
  { label: "Bespoke Jewellery", to: "/bespoke" },
  { label: "Our Services", to: "/services" },
  { label: "About Us", to: "/about" },
  { label: "Metal Information", to: "/metals" },
  { label: "Contact", to: "/contact" },
];

function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (q.length < 2) return setSuggestions([]);
      try {
        const { data } = await api.get("/search/suggest", { params: { q } });
        setSuggestions(data.items);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    onClose();
    navigate(`/shop/all?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-xl"
          data-testid="search-overlay"
        >
          <div className="max-w-3xl mx-auto pt-32 px-6">
            <button onClick={onClose} data-testid="search-close" className="absolute top-8 right-8 text-stone-mist hover:text-gold transition-colors">
              <X className="w-7 h-7" />
            </button>
            <form onSubmit={submit}>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search jewellery, gifts, collections..."
                data-testid="search-input"
                className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none font-serif text-3xl sm:text-4xl text-ivory placeholder:text-stone-mist/50 py-4"
              />
            </form>
            <div className="mt-8 space-y-1">
              {suggestions.map((s) => (
                <Link
                  key={s.slug}
                  to={`/product/${s.slug}`}
                  onClick={onClose}
                  data-testid={`search-suggestion-${s.slug}`}
                  className="flex items-center gap-4 py-3 border-b border-white/5 group"
                >
                  {s.image && <img src={s.image} alt="" className="w-10 h-10 object-cover" />}
                  <span className="text-ivory group-hover:text-gold transition-colors font-light">{s.name}</span>
                  <span className="ml-auto font-mono text-xs text-gold">{gbp(s.price)}</span>
                </Link>
              ))}
              {q.length >= 2 && suggestions.length === 0 && (
                <p className="text-stone-mist text-sm py-4">No matches yet — press Enter to search the whole shop.</p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const { cartCount, wishlist, setCartOpen } = useShop();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-50">
        <div className="bg-ink-2 border-b border-gold/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-stone-mist">
            <span className="hidden sm:block">Family-run since 1982 — Peel, Isle of Man</span>
            <Link to="/#discounts" data-testid="topbar-discount-link" className="hidden md:flex items-center gap-2 text-gold hover:text-gold-glow transition-colors">
              Blue Light, Veterans & TT Marshals — 10% off
            </Link>
            <a href={`tel:${CONTACT.phoneShort.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-gold transition-colors" data-testid="header-phone">
              <Phone className="w-3 h-3 text-gold" /> {CONTACT.phone}
            </a>
          </div>
        </div>
        <header className={`transition-all duration-500 ${scrolled ? "glass-dark border-b border-gold/20" : "bg-transparent"}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
                <Triskelion className="w-8 h-8 text-gold transition-transform duration-700 group-hover:rotate-[120deg]" />
                <span className="font-serif text-2xl tracking-wide text-ivory">
                  Celtic <span className="text-gold">Gold</span>
                </span>
              </Link>

              <nav className="hidden xl:flex items-center gap-7" data-testid="main-nav">
                {NAV.map((item) =>
                  item.mega ? (
                    <div key={item.label} className="relative group">
                      <NavLink
                        to={item.to}
                        data-testid="nav-shop"
                        className={({ isActive }) =>
                          `flex items-center gap-1 text-[13px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                            isActive ? "text-gold" : "text-ivory/80 hover:text-gold"
                          }`
                        }
                      >
                        {item.label}
                        <ChevronDown className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180" />
                      </NavLink>
                      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                        <div className="glass-dark border border-gold/20 p-6 w-[720px] grid grid-cols-4 gap-4" data-testid="mega-menu">
                          {SHOP_CATEGORIES.map((cat) => (
                            <Link
                              key={cat.slug}
                              to={cat.href || `/shop/${cat.slug}`}
                              data-testid={`mega-${cat.slug}`}
                              className="group/card block"
                            >
                              <div className="overflow-hidden mb-3 aspect-[4/3]">
                                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110" />
                              </div>
                              <p className="text-ivory text-sm group-hover/card:text-gold transition-colors">{cat.name}</p>
                            </Link>
                          ))}
                          <Link
                            to="/shop/all"
                            data-testid="mega-shop-all"
                            className="flex items-center justify-center border border-gold/30 text-gold text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300"
                          >
                            Shop All
                          </Link>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `text-[13px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                          isActive ? "text-gold" : "text-ivory/80 hover:text-gold"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  )
                )}
              </nav>

              <div className="flex items-center gap-4 sm:gap-5 ml-6 pl-6 border-l border-white/10">
                <button onClick={() => setSearchOpen(true)} data-testid="search-toggle" className="text-ivory/80 hover:text-gold transition-colors" aria-label="Search">
                  <Search className="w-5 h-5" />
                </button>
                <Link to="/account" data-testid="account-link" className="hidden sm:block text-ivory/80 hover:text-gold transition-colors" aria-label="Account" title="Your account">
                  <User className="w-5 h-5" />
                </Link>
                <Link to="/shop/all?wishlist=1" data-testid="wishlist-link" className="relative hidden sm:block text-ivory/80 hover:text-gold transition-colors" aria-label="Wishlist">
                  <Heart className="w-5 h-5" />
                  {wishlist.length > 0 && (
                    <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-gold text-ink text-[10px] flex items-center justify-center font-medium">{wishlist.length}</span>
                  )}
                </Link>
                <button onClick={() => setCartOpen(true)} data-testid="cart-toggle" className="relative text-ivory/80 hover:text-gold transition-colors" aria-label="Basket">
                  <ShoppingBag className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span data-testid="cart-count" className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-gold text-ink text-[10px] flex items-center justify-center font-medium">{cartCount}</span>
                  )}
                </button>
                <Link
                  to="/contact"
                  data-testid="enquire-btn"
                  className="hidden lg:inline-flex border border-gold/50 text-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300"
                >
                  Enquire
                </Link>
                <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle" className="xl:hidden text-ivory" aria-label="Menu">
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 right-0 z-[60] w-full sm:w-96 bg-ink-2 border-l border-gold/20 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <span className="font-serif text-xl text-ivory">Menu</span>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="text-stone-mist hover:text-gold">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-6 space-y-1">
              {NAV.map((item) =>
                item.mega ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setShopOpen(!shopOpen)}
                      data-testid="mobile-shop-toggle"
                      className="w-full flex items-center justify-between py-3 text-ivory uppercase tracking-[0.15em] text-sm"
                    >
                      Shop
                      <ChevronDown className={`w-4 h-4 text-gold transition-transform ${shopOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {shopOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          {SHOP_CATEGORIES.map((cat) => (
                            <Link
                              key={cat.slug}
                              to={cat.href || `/shop/${cat.slug}`}
                              onClick={() => setMobileOpen(false)}
                              data-testid={`mobile-shop-${cat.slug}`}
                              className="block py-2.5 pl-4 text-stone-mist hover:text-gold text-sm"
                            >
                              {cat.name}
                            </Link>
                          ))}
                          <Link to="/shop/all" onClick={() => setMobileOpen(false)} data-testid="mobile-shop-all" className="block py-2.5 pl-4 text-gold text-sm">
                            Shop All
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-3 text-ivory uppercase tracking-[0.15em] text-sm hover:text-gold"
                  >
                    {item.label}
                  </Link>
                )
              )}
              <div className="pt-6 space-y-3">
                <Link to="/#discounts" onClick={() => setMobileOpen(false)} data-testid="mobile-discount-link" className="block text-center border border-gold text-gold py-3.5 text-xs uppercase tracking-[0.25em]">
                  Blue Light, Veterans & TT Marshals — 10% off
                </Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} data-testid="mobile-enquire" className="block text-center bg-gold text-ink py-3.5 text-xs uppercase tracking-[0.25em] font-medium">
                  Enquire
                </Link>
                <a href={`tel:${CONTACT.phoneShort.replace(/\s/g, "")}`} data-testid="mobile-call" className="block text-center border border-gold/40 text-gold py-3.5 text-xs uppercase tracking-[0.25em]">
                  Call {CONTACT.phoneShort}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
